import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  ApiClientError,
  apiClient,
  type GenerationHistoryQuery
} from "../api/client.js";
import type { SeedanceGenerationHistoryResponse } from "@seedance/contracts";
import { useAuth } from "../auth/AuthProvider.js";
import { MemberGenerationFeedback } from "./MemberGenerationFeedback.js";

type HistoryState =
  | { status: "loading" }
  | { status: "ready"; data: Awaited<ReturnType<typeof apiClient.listMyGenerationHistory>> }
  | { status: "error"; message: string };

const stateLabel: Record<string, string> = {
  queued: "排队中",
  leased: "已分配",
  submitting: "正在提交",
  submitted: "已提交",
  running: "生成中",
  submit_uncertain: "等待管理员核对",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消"
};

function exactTokens(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/gu, ",");
}

function time(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
  }).format(new Date(value));
}

type GenerationHistoryItem = SeedanceGenerationHistoryResponse["items"][number];

export function GenerationHistoryRows(props: {
  items: readonly GenerationHistoryItem[];
  insertingOutputAssetId: string | undefined;
  onInsertOutput(item: GenerationHistoryItem, outputAssetId: string): void;
  onFeedback?(generationId: string): void;
}) {
  return <>
    {props.items.map((item) => <tr key={item.id}>
      <td>{time(item.createdAt)}</td><td>{item.projectName}</td><td>{typeof item.snapshot.request === "object" && item.snapshot.request !== null && "model" in item.snapshot.request ? String(item.snapshot.request.model) : "Seedance"}</td>
      <td>{stateLabel[item.state] ?? item.state}</td>
      <td>{item.usage.totalTokens !== undefined ? exactTokens(item.usage.totalTokens) : "上游未提供"}</td>
      <td>{item.outputAssetIds.length === 0 ? "已归档" : item.outputAssetIds.map((outputAssetId) => (
        <button
          className="foundation-button foundation-button--ghost"
          type="button"
          key={outputAssetId}
          aria-label={`插入生成视频 ${outputAssetId} 到原画布`}
          disabled={props.insertingOutputAssetId === outputAssetId}
          onClick={() => props.onInsertOutput(item, outputAssetId)}
        >{props.insertingOutputAssetId === outputAssetId ? "正在插入…" : "插入到原画布"}</button>
      ))}{props.onFeedback && <button className="foundation-button foundation-button--ghost" type="button" onClick={() => props.onFeedback?.(item.id)}>失败证据与处理进度</button>}</td>
    </tr>)}
  </>;
}

export function GenerationHistoryPage() {
  const navigate = useNavigate();
  const { state: authState, recoverAuthentication } = useAuth();
  const [history, setHistory] = useState<HistoryState>({ status: "loading" });
  const [filter, setFilter] = useState<GenerationHistoryQuery["state"]>();
  const [nextCursor, setNextCursor] = useState<string>();
  const [insertingOutputAssetId, setInsertingOutputAssetId] = useState<string>();
  const [outputNotice, setOutputNotice] = useState<string>();
  const [feedbackId, setFeedbackId] = useState<string>();

  const load = useCallback(async (cursor?: string) => {
    try {
      const data = await apiClient.listMyGenerationHistory({
        ...(filter === undefined ? {} : { state: filter }),
        ...(cursor === undefined ? {} : { cursor }),
        limit: 50
      });
      setHistory((current) => cursor && current.status === "ready"
        ? {
            status: "ready",
            data: {
              ...data,
              // The first page summary already covers the whole active filter.
              // Cursor pages only extend rows; replacing it would make totals shrink.
              summary: current.data.summary,
              items: [...current.data.items, ...data.items]
            }
          }
        : { status: "ready", data });
      setNextCursor(data.nextCursor);
    } catch (error) {
      if (recoverAuthentication(error)) return;
      setHistory({
        status: "error",
        message: error instanceof ApiClientError ? `加载失败（请求 ${error.requestId}）。` : "网络连接不可用，请稍后重试。"
      });
    }
  }, [filter, recoverAuthentication]);

  useEffect(() => { void load(); }, [load]);
  const insertOutput = useCallback(async (item: GenerationHistoryItem, outputAssetId: string) => {
    setInsertingOutputAssetId(outputAssetId);
    setOutputNotice(undefined);
    try {
      const canvas = await apiClient.getCanvas(item.projectId);
      await apiClient.insertGenerationOutput({
        generationId: item.id,
        outputAssetId,
        canvasRevision: canvas.revision
      });
      navigate(`/projects/${item.projectId}`);
    } catch (error) {
      if (recoverAuthentication(error)) return;
      setOutputNotice(
        error instanceof ApiClientError && error.code === "CANVAS_VERSION_CONFLICT"
          ? "画布刚刚更新，结果尚未插入；请重新打开记录后再试。"
          : "结果视频仍已安全保存在我的素材中；插入画布失败，请稍后重试。"
      );
    } finally {
      setInsertingOutputAssetId(undefined);
    }
  }, [navigate, recoverAuthentication]);
  if (authState.status !== "authenticated") return null;

  const data = history.status === "ready" ? history.data : undefined;
  return (
    <main className="foundation-placeholder generation-history-page">
      <header className="foundation-placeholder__header">
        <div>
          <p className="foundation-eyebrow">个人用量与生成状态</p>
          <h1>我的生成记录</h1>
        </div>
        <button className="foundation-button foundation-button--ghost" type="button" onClick={() => navigate("/projects")}>返回项目</button>
      </header>
      <section className="generation-history-page__content" aria-label="我的生成记录内容">
        <div className="generation-history-summary">
          <article><span>V2 已知总 Token</span><strong>{data ? exactTokens(data.summary.totalTokens) : "—"}</strong></article>
          <article><span>V2 已知输入 Token</span><strong>{data ? exactTokens(data.summary.inputTokens) : "—"}</strong></article>
          <article><span>V2 已知输出 Token</span><strong>{data ? exactTokens(data.summary.outputTokens) : "—"}</strong></article>
          <article><span>上游未提供</span><strong>{data?.summary.providerNotReportedCount ?? "—"}</strong></article>
        </div>
        <div className="generation-history-controls">
          <label>状态
            <select value={filter ?? ""} onChange={(event) => setFilter((event.currentTarget.value || undefined) as GenerationHistoryQuery["state"])}>
              <option value="">全部</option>
              {Object.entries(stateLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <button className="foundation-button foundation-button--ghost" type="button" onClick={() => void load()}>刷新</button>
        </div>
        {outputNotice ? <p className="foundation-muted" role="status">{outputNotice}</p> : null}
        {history.status === "loading" ? <div className="foundation-state-card"><span className="foundation-spinner" aria-hidden="true" /><strong>正在加载生成记录…</strong></div> : null}
        {history.status === "error" ? <div className="foundation-state-card" role="alert"><strong>{history.message}</strong><button className="foundation-button foundation-button--ghost" type="button" onClick={() => void load()}>重试</button></div> : null}
        {data ? (
          <div className="generation-history-table-wrap">
            <table className="generation-history-table">
              <thead><tr><th>时间</th><th>项目</th><th>模型</th><th>状态</th><th>本次 Token</th><th>生成输出</th></tr></thead>
              <tbody><GenerationHistoryRows items={data.items} insertingOutputAssetId={insertingOutputAssetId} onInsertOutput={(item, outputAssetId) => void insertOutput(item, outputAssetId)} onFeedback={setFeedbackId} /></tbody>
            </table>
            {data.items.length === 0 ? <p className="foundation-muted">还没有生成任务；以后每次提交都会在这里保留状态与 Token 记录。</p> : null}
          </div>
        ) : null}
        {nextCursor ? <button className="foundation-button foundation-button--ghost" type="button" onClick={() => void load(nextCursor)}>加载更多</button> : null}
        {feedbackId && <MemberGenerationFeedback key={feedbackId} generationId={feedbackId} onClose={() => setFeedbackId(undefined)} />}
      </section>
    </main>
  );
}
